import React, { useEffect, useState, useRef, useCallback } from "react";
import backgroundImage from "../assets/instruction_page/signin_2.jpg";
import styles from "./SignIn.module.css";

function SignIn() {
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Parallax effect on mouse movement
  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    const x = (clientX - innerWidth / 2) / innerWidth;
    const y = (clientY - innerHeight / 2) / innerHeight;
    setMousePosition({ x: x * 20, y: y * 20 });
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [handleMouseMove]);

  // Fade-in animation on mount
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Zoho authentication
  useEffect(() => {
    if (window.catalyst) {
      const config = {
        signin_providers_only: true,
        service_url: "/app/#/index.html",
      };
      window.catalyst.auth.signIn("loginDivElementId", config);
    }

    const pollInterval = 75;
    const maxWaitMs = 5000;
    const markReady = () => setLoading(false);
    const timeoutId = setTimeout(markReady, maxWaitMs);

    const intervalId = setInterval(() => {
      const iframe = document.querySelector("#loginDivElementId iframe");
      if (!iframe) return;

      const checkInside = () => {
        try {
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          if (doc && doc.querySelector(".zoho_fed_box")) {
            markReady();
            return true;
          }
        } catch (e) {
          // cross-origin; ignore
        }
        return false;
      };

      if (iframe.complete && checkInside()) {
        clearInterval(intervalId);
        return;
      }

      iframe.addEventListener(
        "load",
        () => {
          if (checkInside()) {
            clearInterval(intervalId);
          }
        },
        { once: true }
      );

      clearInterval(intervalId);
    }, pollInterval);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, []);

  // Generate particles for ambient effect
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    animationDelay: `${Math.random() * 15}s`,
    animationDuration: `${10 + Math.random() * 10}s`,
  }));

  return (
    <div className={styles.container} ref={containerRef}>
      {/* Static Background Layer */}
      <div
        className={styles.backgroundLayer}
        style={{
          backgroundImage: `url(${backgroundImage})`,
        }}
      />

      {/* Ambient Overlay */}
      <div className={styles.ambientOverlay} />

      {/* Animated Light Rays */}
      <div className={styles.lightRays} />

      {/* Floating Particles */}
      <div className={styles.particles}>
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={styles.particle}
            style={{
              left: particle.left,
              animationDelay: particle.animationDelay,
              animationDuration: particle.animationDuration,
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className={`${styles.contentWrapper} ${isVisible ? styles.visible : ""}`}>
        {/* Sign-In Card - positioned in the red marked area */}
        <div className={styles.signInCard}>
          {/* Description */}
          <div className={styles.cardHeader}>
            <p className={styles.cardDescription}>
              Step into a thrilling mission where you take on Deluge challenges and capture the escaped dinosaurs across the world.
            </p>
          </div>

          {/* Login Container */}
          <div className={styles.loginContainer}>
            {loading && (
              <div className={styles.loadingOverlay}>
                <div className={styles.spinner} />
                <span className={styles.loadingText}>Loading...</span>
              </div>
            )}
            <div
              id="loginDivElementId"
              className={`${styles.loginContent} ${loading ? styles.hidden : styles.visible}`}
              style={{ minHeight: "100px" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignIn;
